import { useState } from 'react';
import { history } from 'umi';
import { useMemoizedFn } from 'ahooks';
import usePageLog, { reportBtnOperationLog } from '@gp/hooks/usePageLog';
import useDPSReportMomentLocale from '../../_hooks/useDPSReportMomentLocale';
import { fetchFullyProjects, fetchCalendars, editDPSReport } from '@gp/services/dpsReport/v3';
import { addUuidBatch, addDays, messageTips, getFirstDayOfMonth, getLastDayOfMonth } from '@gp/utils/common';
// @ts-ignore
import { storage } from '@gp/utils/utils';
import { getWeekDateOfDPSReport, getCommonRepeatCheckKey } from '../../utils';
import { getCalendarEndDate } from '../utils';
import { refreshCollection } from '../utils/refreshData';
import { BoolFlag } from '@gp/enums/common';
import { DPS_REPORT_STORAGE_KEY } from '@gp/constants/storageKeys';

import type { IDateRange } from '@home/types/common';
import type {
  IDPSReportCalendarProjectSimpleVO,
  IDPSReportCalendarDTO,
  IDPSReportFilter,
  IDPSReportHelperVO,
} from '@gp/types/dpsReport/common';
import type { IDPSReportModuleTitles } from '@gp/types/dpsReport/v3';
import type { IDPSReportFormValue } from '../types';

const REMOVE_KEYS = ['mock_id'];

const projectRequiredFilter = <T extends IDPSReportCalendarProjectSimpleVO>(payload?: T): boolean => {
  const { project_id: projectId } = payload || {};
  return !!projectId;
};

const formatReportModuleParams = (list?: any[], filter = projectRequiredFilter): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.filter(filter).map((item) => {
    const keys = Object.keys(item).filter((key) => !REMOVE_KEYS.includes(key));
    return keys.reduce<any>((acc, key) => {
      acc[key] = item[key];
      return acc;
    }, {});
  });
};

const createReportHelper = (filter?: IDPSReportFilter): IDPSReportHelperVO | null => {
  const { start_date: startDate, end_date: endDate } = filter || {};
  if (!startDate || !endDate) return null;
  const {
    startDateM: startDateMDPSReportWeek,
    startDate: startDateDPSReportWeek,
    endDate: endDateDPSReportWeek,
  } = getWeekDateOfDPSReport(addDays(startDate, 1), addDays(endDate, 1));
  return {
    // 开始日期（DPS周报要求周一作为一周的开始日期）
    start_date_dps_report_week: startDateDPSReportWeek,
    // 结束日期（DPS周报要求周五作为一周的结束日期）
    end_date_dps_report_week: endDateDPSReportWeek,
    // 年份（当前周的年份）
    year: startDateMDPSReportWeek?.get('year') || Number.NaN,
    // 周数（当前周是当前年中的第几周）
    weeks_in_year: startDateMDPSReportWeek?.weeks() || Number.NaN,
  };
};

export const getRefreshedCalendarsData = async (params: {
  current: IDPSReportCalendarDTO[];
  start_date: string;
  end_date: string;
}) => {
  const { current, start_date: startDate, end_date: endDate } = params || {};
  const initialCalendarsRes = await fetchCalendars({
    start_date: getFirstDayOfMonth(startDate),
    end_date: getLastDayOfMonth(endDate),
  });
  const initialCalendars = addUuidBatch(initialCalendarsRes?.data?.list);

  return refreshCollection({
    current,
    initial: initialCalendars,
    // ignoreKeys: ['status'],
    ignoreKeys: [],
    getRepeatCheckKey: getCommonRepeatCheckKey,
  });
};

export default function useOperateDPSReport(props: {
  pageId: string;
  pageLogOthers?: {
    uidType: string;
    uid: string;
    extInfo: string;
  };
  confirmBtnId: string;
  publishBtnId: string;
  refreshCalendarBtnId: string;
  // 获取初始化筛选器数据
  getInitialFilter: () => IDPSReportFilter;
  // 更新周报数据
  updateReportPage: (filter: IDPSReportFilter, calendarsDates: IDateRange) => void;
  // 获取当前周报的value
  getReport?: () => IDPSReportFormValue;
  // 设置当前周报的value
  setReport?: (payload: Partial<IDPSReportFormValue>) => void;
}) {
  const {
    pageId,
    pageLogOthers,
    confirmBtnId,
    publishBtnId,
    refreshCalendarBtnId,
    getInitialFilter,
    updateReportPage,
    getReport,
    setReport,
  } = props;

  const [isGuide, setIsGuide] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalendarInnerLoading, setIsCalendarInnerLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitType, setSubmitType] = useState<BoolFlag>(BoolFlag.False);

  const [filter, setFilter] = useState<IDPSReportFilter>({
    start_date: '',
    end_date: '',
  });

  const [projects, setProjects] = useState<IDPSReportCalendarProjectSimpleVO[]>([]);

  const reportHelper = createReportHelper(filter);

  const updateProjects = useMemoizedFn(async () => {
    const res = await fetchFullyProjects();
    const { list } = res?.data || {};
    setProjects(list || []);
  });

  const updateReport = useMemoizedFn(async (filter: IDPSReportFilter, calendarsDates: IDateRange) => {
    updateReportPage(filter, calendarsDates);
  });

  const handleCalendarRefreshData = async () => {
    try {
      setIsCalendarInnerLoading(true);
      const currentCalendarData = getReport?.()?.calendar || {};
      const {
        start_date: currentCalendarsStartDate,
        end_date: currentCalendarsEndDate,
        list: currentCalendars,
      } = currentCalendarData || ({} as any);

      const refreshedCalendarsData = await getRefreshedCalendarsData({
        ...currentCalendarData,
        current: currentCalendars,
        start_date: currentCalendarsStartDate,
        end_date: currentCalendarsEndDate,
      });

      setReport?.({
        calendar: {
          ...currentCalendarData,
          start_date: currentCalendarsStartDate || '',
          end_date: currentCalendarsEndDate || '',
          list: refreshedCalendarsData || [],
        },
      });
    } finally {
      setIsCalendarInnerLoading(false);
    }

    reportBtnOperationLog(refreshCalendarBtnId);
  };

  const handleSubmit = useMemoizedFn(
    async (
      titles: IDPSReportModuleTitles,
      report: IDPSReportFormValue,
      isPublish: boolean,
      cfg: {
        reportId?: string;
        tips: string;
      },
    ) => {
      try {
        const { reportId, tips } = cfg || {};
        const { start_date: startDate, end_date: endDate } = filter || {};
        const { comment, project_comment: projectComment, changes, memo, calendar: calendarsData } = report || {};
        const { start_date: calendarsStartDate, end_date: calendarsEndDate, list: calendars } = calendarsData || {};
        const submitTypeTmp = isPublish ? BoolFlag.True : BoolFlag.False;
        const params = {
          ...titles,
          report_id: reportId || '',
          is_publish: submitTypeTmp,
          start_date: startDate || '',
          end_date: endDate || '',
          comment: comment || '',
          project_comment: formatReportModuleParams(projectComment),
          changes: formatReportModuleParams(changes),
          memo: formatReportModuleParams(memo),
          calendar: {
            start_date: getFirstDayOfMonth(calendarsStartDate) || '',
            end_date: getLastDayOfMonth(calendarsEndDate) || '',
            list: formatReportModuleParams(calendars),
          },
        };
        setIsSubmitting(true);
        setSubmitType(submitTypeTmp);
        const res = await editDPSReport(params);
        messageTips('success', tips);
        setIsGuide(false);
        storage.setSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_ID, res?.data?.report_id || '');
        history.goBack();
      } catch (exception: any) {
        messageTips('error', exception?.message || '');
      } finally {
        setIsSubmitting(false);
        reportBtnOperationLog(isPublish ? publishBtnId : confirmBtnId);
      }
    },
  );

  const init = () => {
    const initialFilter = { ...filter, ...getInitialFilter() };
    const { start_date: initialStartDate } = initialFilter || {};
    const initialCalendarsDates = {
      start_date: getFirstDayOfMonth(initialStartDate),
      end_date: getCalendarEndDate(initialStartDate),
    };
    if (!!initialFilter.start_date && !!initialFilter.end_date) {
      const report = getReport?.() || {};
      // @ts-ignore
      const reportCalendars = report?.calendar || {};
      const reportCalendarsTmp = { ...reportCalendars, ...initialCalendarsDates };
      const reportTmp = { ...report, calendar: reportCalendarsTmp };
      setReport?.(reportTmp);
      updateReport(initialFilter, initialCalendarsDates);
    } else {
      setIsLoading(false);
    }
    updateProjects();
    setFilter(initialFilter);
  };

  useDPSReportMomentLocale(init);

  usePageLog({ pageId, ...pageLogOthers });

  return {
    isGuide,
    isLoading,
    setIsLoading,
    isCalendarInnerLoading,
    setIsCalendarInnerLoading,
    isSubmitting,
    submitType,
    reportHelper,
    projects,
    filter,
    setFilter,
    updateReport,
    onCalendarRefreshData: handleCalendarRefreshData,
    onSubmit: handleSubmit,
  };
}
